import React, { useState } from 'react';
import { Camera, Check, Trash2, ScanFace } from 'lucide-react';
import PhotoActionModal from '../PhotoActionModal';
import PhotoCaptureModalEnhanced, { PhotoCaptureResult } from '../PhotoCaptureModalEnhanced';
import PhotoIPCameraModal, { PhotoResult } from '../PhotoIPCameraModal';

// Keep backward-compat alias so callers using PhotoResult still compile
export type { PhotoResult };
export type PhotoSectionCaptureResult = PhotoCaptureResult;

interface PhotoSectionProps {
  photo: { base64: string; faceDetected?: boolean; width?: number; height?: number } | null;
  onPhotoCapture: (photo: { base64: string; blob: Blob; faceDetected: boolean; width: number; height: number; faceConfidence?: number }) => void;
  onPhotoClear: () => void;
  title: string;
  disabled?: boolean;
  darkMode?: boolean;
  customerId?: string;
  filterCategory?: string;
}

export const PhotoSection: React.FC<PhotoSectionProps> = ({
  photo,
  onPhotoCapture,
  onPhotoClear,
  title,
  disabled = false,
  darkMode = false,
  customerId,
  filterCategory = 'captura_facial',
}) => {
  const [showActionModal, setShowActionModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showIPCameraModal, setShowIPCameraModal] = useState(false);
  const [photoModalTab, setPhotoModalTab] = useState<0 | 1>(0);

  const openWithTab = (tab: 0 | 1) => {
    setShowActionModal(false);
    setPhotoModalTab(tab);
    setShowPhotoModal(true);
  };

  const handleResult = (result: PhotoCaptureResult) => {
    onPhotoCapture({
      base64: result.base64,
      blob: result.blob,
      faceDetected: result.faceDetected || false,
      width: result.width,
      height: result.height,
      faceConfidence: result.faceConfidence,
    });
    setShowPhotoModal(false);
  };

  const handleIPCameraResult = (result: PhotoResult) => {
    onPhotoCapture({
      base64: result.base64,
      blob: result.blob,
      faceDetected: result.faceDetected || false,
      width: result.width,
      height: result.height,
      faceConfidence: result.faceConfidence,
    });
    setShowIPCameraModal(false);
  };

  return (
    <div className="flex flex-col items-center">
      {/* Photo Container */}
      <div className="relative">
        {photo ? (
          <div className="relative">
            <img
              src={photo.base64}
              alt="Foto capturada"
              className={`w-32 h-40 rounded-xl object-cover border-4 ${
                darkMode ? 'border-gray-700' : 'border-gray-200'
              }`}
            />
            {photo.faceDetected && (
              <div className="absolute bottom-1 right-1 bg-green-500 text-white p-1.5 rounded-full shadow-md">
                <Check className="h-3.5 w-3.5" />
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowActionModal(true)}
            disabled={disabled}
            className={`w-32 h-40 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              darkMode
                ? 'border-gray-600 bg-gray-800 hover:border-gray-500 hover:bg-gray-700'
                : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
            }`}
          >
            <Camera className={`h-8 w-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <span className={`text-xs text-center px-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Adicionar Foto</span>
          </button>
        )}
      </div>

      {/* Title */}
      <p className={`mt-3 text-xs font-medium text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{title}</p>

      {/* Face Detection Status */}
      {photo && (
        <div className="mt-1 flex flex-col items-center gap-2">
          <div className={`flex items-center gap-1 text-xs ${
            photo.faceDetected
              ? (darkMode ? 'text-green-400' : 'text-green-600')
              : (darkMode ? 'text-amber-400' : 'text-amber-600')
          }`}>
            {photo.faceDetected ? (
              <>
                <Check className="h-3 w-3" />
                <span>Rosto detectado</span>
              </>
            ) : (
              <span>Rosto não detectado</span>
            )}
          </div>

          {!photo.faceDetected && (
            <button
              type="button"
              onClick={() => setShowActionModal(true)}
              disabled={disabled}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                darkMode
                  ? 'text-blue-400 bg-blue-900/20 border-blue-700 hover:bg-blue-900/30'
                  : 'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100'
              }`}
            >
              <ScanFace className="h-3.5 w-3.5" />
              <span>Detectar Rosto</span>
            </button>
          )}
        </div>
      )}

      {/* Delete */}
      {photo && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={onPhotoClear}
            disabled={disabled}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              darkMode
                ? 'text-red-400 bg-red-900/20 border-red-700 hover:bg-red-900/30'
                : 'text-red-600 bg-red-50 border-red-200 hover:bg-red-100'
            }`}
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span>Remover</span>
          </button>
        </div>
      )}

      {/* Method picker */}
      <PhotoActionModal
        open={showActionModal}
        onClose={() => setShowActionModal(false)}
        onUpload={() => openWithTab(0)}
        onWebcam={() => openWithTab(1)}
        onIPCamera={() => {
          setShowActionModal(false);
          setShowIPCameraModal(true);
        }}
        title={title}
        darkMode={darkMode}
      />

      {/* Upload / Webcam modal */}
      {showPhotoModal && (
        <PhotoCaptureModalEnhanced
          open={showPhotoModal}
          onClose={() => setShowPhotoModal(false)}
          onConfirm={handleResult}
          title={title}
          cropShape="rect"
          aspectRatio={4 / 5}
          customerId={customerId}
          initialTab={photoModalTab}
          hideIPCamera
        />
      )}

      {/* IP Camera modal — same rich component used for visitantes */}
      {showIPCameraModal && (
        <PhotoIPCameraModal
          open={showIPCameraModal}
          onClose={() => setShowIPCameraModal(false)}
          onConfirm={handleIPCameraResult}
          title={title}
          customerId={customerId}
          filterCategory={filterCategory}
          cropShape="rect"
          aspectRatio={4 / 5}
        />
      )}
    </div>
  );
};

export default PhotoSection;
